import { useState, useId, useCallback, useRef } from 'react';
import './userDocuments.css';
import { launchError, launchSuccess } from 'components/notifications';
import { useTranslation } from 'react-i18next';
import { deleteUserDocument, uploadUserDocument } from 'services/documentServices';
import { fetchUserAgreement } from 'services/fetchUserById';
import { downloadFile } from 'utils/helpers';
import { doc_types_display_map } from 'utils/display';
import { COMPANY_DOC_TYPES, USER_DOC_TYPES } from 'utils/types';
import { formatIBAN } from 'utils/iban';
import Modal from 'components/modal/Modal';

const USER_SELECT_OPTIONS = {
  passport: 'Passeport',
  utility_bill: 'Justificatif de domicile',
  ibans: 'Iban'
}

const IBAN_REGEX = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;

const isValidIban = (iban) => IBAN_REGEX.test(iban.replace(/\s/g, '').toUpperCase());
const COMPANY_SELECT_OPTIONS = {
  kbis: 'Kbis',
  company_status: 'Statuts de l\'entreprise',
  company_report: 'Bilan de l\'entreprise',
  company_other: 'Autres'
}

const UserDocuments = ({ documents, user, id, setDocuments, title = 'Documents', isCompanyDocuments = false }) => {

  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('')
  const [ibanValue, setIbanValue] = useState('')
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({});
  const uploadId = useId();
  const fileInputRef = useRef(null);

  const docTypes = isCompanyDocuments ? COMPANY_DOC_TYPES : USER_DOC_TYPES;
  const selectOptions = isCompanyDocuments ? COMPANY_SELECT_OPTIONS : USER_SELECT_OPTIONS;


  const handleFileChange = useCallback((event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    setFile(event.target.files[0]);
  }, []);

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const isIbanType = fileType === 'ibans';

  const uploadDocument = async () => {
    if (!file || !fileType || isUploading) return;
    if (isIbanType && !isValidIban(ibanValue)) return;
    setIsUploading(true);
    try {
      const currentFile = file;
      const currentFileType = fileType;
      const currentFileName = isIbanType ? ibanValue.replace(/\s/g, '').toUpperCase() : file?.name;

      const base64 = await toBase64(currentFile)
      const path = await uploadUserDocument(user, id, base64, currentFileType, currentFileName);

      const key = `${currentFileType}_pictures`;
      setDocuments(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), path]
      }))
      setFile(null)
      setFileType('');
      setIbanValue('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      launchSuccess(t('notifications.users.userById.upload.success'))
    } catch (error) {
      if (error?.response?.data?.error === 'file_already_exists')
        launchError(t('notifications.users.userById.upload.already_exists_error'))
      else
        launchError(t('notifications.users.userById.upload.error'))
    } finally {
      setIsUploading(false)
    }
  }

  const deleteDocument = async (doctype, key) => {

    try {
      const updatedDocuments = { ...documents };
      const index = updatedDocuments[doctype].findIndex(url => url === key);
      await deleteUserDocument(user, id, key);
      updatedDocuments[doctype].splice(index, 1);
      setDocuments(updatedDocuments);
      setIsModalOpen(false)
      setSelectedDocument({})

      launchSuccess(t('notifications.users.userById.upload.success'))
    } catch (error) {
      launchError(t('notifications.users.userById.upload.error'))
    } finally {
      setIsUploading(false)
    }
  }

  // Les agreements sont générés par le back (PMA) : téléchargement uniquement, pas de suppression
  const downloadAgreement = async (filename) => {
    try {
      const data = await fetchUserAgreement(user, id, filename)
      downloadFile({ data, type: 'pdf', filename: `${filename.replace('.pdf', '')}_${id}.pdf` })
    } catch (error) {
      console.error(error)
      launchError('Erreur download link')
    }
  }

  function removeUndefinedPrefix(str) {
    return str.replace(/^undefined\//, '');
  }

  const hasDocuments = () => docTypes.some(type => documents?.[type]?.length || 0) > 0

  const getPictureFromType = (docType, item, index) => {
    switch (docType) {
      case 'idDocs':
        return <img src={`data:image/png;base64,%20${item}`} alt="profil img" className='userData_doc_item' />
      case 'ibans_pictures':
        return <div>
          <IbanDocument src={removeUndefinedPrefix(item)} onFullscreen={() => setShowFullScreen(prevState => prevState === item ? false : item)} />
          <label className='delete_document_button' onClick={() => openModal(docType, item)}>Supprimer X</label>
        </div>
      case 'agreements':
        return <div className='delete_non_image_box'>
          <button type='button' className='agreement_download_button' onClick={() => downloadAgreement(item.filename)}>
            {item.filename.replace('.pdf', '').replace('pma_', 'PMA ')}
          </button>
        </div>
      default:
        return <div className='delete_non_image_box'>
          <a href={item} target='_blank' rel='noopener noreferrer'>{displayFileName(item, docType)} </a>
          <label className='delete_document_button' onClick={() => openModal(docType, item)}>X</label>
        </div>
    }
  }

  const displayFileName = (key, type) => {
    const type_renamed = type.replace('_pictures', '')
    const index = key.indexOf(type_renamed);
    const filename = key.slice(index + type_renamed.length + 1)
    return filename
  }

  const openModal = (docType, key) => {
    setSelectedDocument({ docType, key });
    setIsModalOpen(true)
  }


  return (<div className='userDocuments_container'>
    <div className='userData_image'>
      <p className='user_doc_title'>{title} :</p>
      <div className='user_document_upload'>
        <label htmlFor={uploadId} className='history_export_button'>Choisir un fichier</label>
        <input ref={fileInputRef} id={uploadId} type='file' className='user_file_upload' onChange={handleFileChange} />
        {file &&
          <>
            <label>{file?.name}</label>
            <select className='custom-select' value={fileType} onChange={(e) => { setFileType(e.target.value); setIbanValue(''); }}>
              <option value=''>Type</option>
              {
                Object.entries(selectOptions).map(([key, display], index) =>
                  <option key={`${key}_${index}`} value={key}>{display}</option>
                )
              }
            </select>
            {isIbanType &&
              <input
                type='text'
                className='iban_input'
                placeholder='FR76 XXXX XXXX XXXX XXXX XXXX XXX'
                value={ibanValue ? formatIBAN(ibanValue) : ''}
                onChange={(e) => setIbanValue(e.target.value.replace(/\s/g, ''))}
              />
            }
            <button
              onClick={uploadDocument}
              className='history_export_button'
              disabled={!file || !fileType || isUploading || (isIbanType && !isValidIban(ibanValue))}
            >
              Envoyer le document
            </button>
          </>
        }
      </div>

      <div className='user_documents_types_container'>
        {!hasDocuments() &&
          <p>Aucun document</p>
        }
        {docTypes.map((docType, indexType) => {
          const docsForType = documents?.[docType] || [];

          if (!docsForType.length) return null;

          return (
            <div key={`${docType}_${indexType}`}>
              <p className='user_document_type'>
                {doc_types_display_map[docType]}
              </p>

              <div className='userData_documents_container'>
                {docsForType.map((item, index) => (
                  <div
                    className='userData_documents'
                    key={`${docType}-${item?.filename || item}-${index}`}
                  >
                    {getPictureFromType(docType, item)}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        )}
      </div>

    </div>

    <FullscreenImage
      src={showFullScreen}
      onClose={() => setShowFullScreen(false)}
    />

    <ConfirmDeleteModal
      isModalOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      deleteDoc={deleteDocument}
      document={selectedDocument}
    />

  </div>)
}

const IbanDocument = ({ src, onFullscreen }) => {
  const [isImage, setIsImage] = useState(true);

  if (!isImage) {
    return <iframe src={src} className='iban_document_embed' title='iban document' />;
  }

  return (
    <img
      src={src}
      alt='iban document'
      className='userData_doc_item'
      onClick={onFullscreen}
      onError={() => setIsImage(false)}
    />
  );
};

const FullscreenImage = ({ src, onClose }) => {
  if (!src) return null;

  return (
    <div
      className="fullscreen-overlay active"
      onClick={onClose}
    >
      <div className="fullscreen-image">
        <img
          className="centered-image"
          src={src}
          alt="Full Screen"
        />
      </div>
    </div>
  );
};


export default UserDocuments

const ConfirmDeleteModal = ({ isModalOpen, onClose, document, deleteDoc }) => {

  const { t } = useTranslation()

  const { docType, key } = document

  return (
    <Modal isOpen={isModalOpen} onClose={onClose} closeButton >
      <h2 className='modal-playlist_title'>Clôturer la position</h2>
      <div className="payout-details">


        <div className='warning_deleted'>
          <img src="https://assets-global.website-files.com/612cdb668b06dbb2a51f99ee/651e6e6668d310d564622c79_warning_img.png" alt="" width={25} />
          <span>Êtes-vous sûr de vouloir supprimer ce document ?
          </span>
        </div>
      </div>
      <hr className='modal_hr_button' />
      <div className='close_position_modal_button_div'>
        <button className='close' onClick={onClose}>{t("pages.payouts.buttons.close")}</button>
        <button className='validate' onClick={() => deleteDoc(docType, key)}>Supprimer</button>
      </div>
    </Modal>

  )
}